import { useSelector } from 'react-redux';

import { selectOnboarding } from 'redux/onboadring/selectors';

import { CurrentProduct } from 'types/payments/payments';
import { IPaymentFlow } from './types';

export interface IUseProducts {
    pageInfo: IPaymentFlow;
}

interface IUseProductsReturn {
    productsListWithDiscount: CurrentProduct[];
    productsListWithoutDiscount: CurrentProduct[];
}

const DEFAULT_THRESHOLD_WEIGHT = 10;
const DEFAULT_THRESHOLD_AGE = 55;
const TWO_PRODUCT_SETS_COUNT = 12;

export function useProducts({ pageInfo }: IUseProducts): IUseProductsReturn {
    const { target_weight, current_weight, age } = useSelector(selectOnboarding);

    const setIsPreselectedValue = (list: CurrentProduct[], elementIndex: number) => {
        if (list[elementIndex]) list[elementIndex].is_preselected = true;
    };

    const getProducts = () => {
        let products = pageInfo.products;

        if (products.length === TWO_PRODUCT_SETS_COUNT) {
            const [firstProducts, secondProducts] = [
                products.slice(0, products.length / 2),
                products.slice(products.length / 2),
            ];
            const preselectIndex = firstProducts.findIndex((item) => item.is_preselected);

            if (preselectIndex !== -1) {
                secondProducts[preselectIndex].is_preselected = true;
            }

            const logic = pageInfo?.config?.logic;
            const logicThreshold = pageInfo?.config?.logic_threshold && Number(pageInfo?.config?.logic_threshold);

            if (logic === 'age') {
                products = age > (logicThreshold ?? DEFAULT_THRESHOLD_AGE) ? secondProducts : firstProducts;
            } else if (current_weight && target_weight) {
                const weightDifference = Math.abs(current_weight - target_weight);

                products =
                    weightDifference > (logicThreshold ?? DEFAULT_THRESHOLD_WEIGHT) ? secondProducts : firstProducts;
            } else {
                products = firstProducts;
            }
        }

        return products;
    };

    const getProductsList = () => {
        const products = getProducts();

        const averageProductValue = products.length / 2;
        const elementIndex = products.findIndex((item) => item.is_preselected);

        const productsListWithDiscount = products.filter((product, i) => i < averageProductValue);
        const productsListWithoutDiscount = products.filter((product, i) => i >= averageProductValue);

        setIsPreselectedValue(productsListWithDiscount, elementIndex);
        setIsPreselectedValue(productsListWithoutDiscount, elementIndex);

        return { productsListWithDiscount, productsListWithoutDiscount };
    };

    return getProductsList();
}
